// Book Health worklist — the wire shape of the client-model work items.
//
// The stored row is ID ONLY: it names records and holds no readable text, so a
// production audit can emit it without an exposure of client data. A person
// cannot act on a list of ids, so the controller joins each id to its record
// and gives a LABEL here. That join is the whole reason this projection exists,
// and it is the mapping seam doing its job (ADR-0003).
//
// The enum below restates the ontology `exceptionType` set. The projection
// layer must not import the ontology runtime, so the restatement is the rule
// and not a shortcut.

import { z } from 'zod';

/** The class of the open question. Mirrors the ontology `exceptionType` set. */
export const bookHealthExceptionTypeSchema = z.enum([
    'holder_conflict',
    'holder_candidate_conflict',
    'holder_stored_mismatch',
    'portfolio_holder_unresolved',
    'manager_unresolved',
    'manager_conflict',
    'missing_account_identifier',
    'account_merge_candidate',
    'missing_portfolio_reference',
    'duplicate_account_match_key',
    'duplicate_portfolio_reference',
    'identifier_target_conflict',
]);
export type BookHealthExceptionType = z.infer<typeof bookHealthExceptionTypeSchema>;

export const bookHealthExceptionStatusSchema = z.enum(['open', 'approved', 'dismissed', 'closed']);
export type BookHealthExceptionStatus = z.infer<typeof bookHealthExceptionStatusSchema>;

/** The grain a work item names, on the subject side and on the candidate side. */
export const bookHealthExceptionTargetKindSchema = z.enum([
    'account',
    'portfolio',
    'client',
    'contact',
    'external-identifier',
    /** A manager candidate of a Portfolio work item. */
    'user',
    'source-row',
    /** The id resolves to no record the controller can read. */
    'unknown',
]);
export type BookHealthExceptionTargetKind = z.infer<typeof bookHealthExceptionTargetKindSchema>;

/**
 * One record, named for a person. `label` is the line a reader identifies the
 * record by; `detail` is the second line that says why it is a candidate, for
 * example the holder of a rival Account.
 */
export const bookHealthExceptionLabelSchema = z.object({
    kind: bookHealthExceptionTargetKindSchema,
    id: z.string(),
    label: z.string(),
    detail: z.string().nullable(),
});
export type BookHealthExceptionLabel = z.infer<typeof bookHealthExceptionLabelSchema>;

/** One work item, enriched for the worklist. */
export const bookHealthExceptionSchema = z.object({
    id: z.string(),
    exceptionType: bookHealthExceptionTypeSchema,
    status: bookHealthExceptionStatusSchema,
    /** The subject record, named. */
    target: bookHealthExceptionLabelSchema,
    /** The candidate records, named, in the stored order. */
    related: z.array(bookHealthExceptionLabelSchema),
    /** The upload that raised the item, when a door named one. */
    sourceUploadId: z.string().nullable(),
    /** The record the decision names, and its label when it resolves. */
    decidedTargetId: z.string().nullable(),
    decidedTarget: bookHealthExceptionLabelSchema.nullable(),
    decidedByUserId: z.string().nullable(),
    decidedByName: z.string().nullable(),
    decidedAt: z.string().nullable(),
});
export type BookHealthException = z.infer<typeof bookHealthExceptionSchema>;

export const listBookHealthExceptionsQuerySchema = z.object({
    /** The default is `open`. `all` gives the history beside the worklist. */
    status: z.enum(['open', 'approved', 'dismissed', 'closed', 'all']).optional(),
});
export type ListBookHealthExceptionsQuery = z.infer<typeof listBookHealthExceptionsQuerySchema>;

export const listBookHealthExceptionsResponseSchema = z.object({
    exceptions: z.array(bookHealthExceptionSchema),
    /** Open items across every class, whatever the filter asked for. */
    openCount: z.number(),
});
export type ListBookHealthExceptionsResponse = z.infer<
    typeof listBookHealthExceptionsResponseSchema
>;

/**
 * The decision a person takes. It carries the real workflow answer and never a
 * server-side default (ADR-0003): a holder decision names the Client, and a
 * target assignment names the record that owns the identifier.
 */
export const bookHealthExceptionDecisionSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('approve_holder'), clientId: z.string().uuid() }),
    z.object({ kind: z.literal('assign_target'), targetId: z.string().uuid() }),
    z.object({ kind: z.literal('dismiss') }),
]);
export type BookHealthExceptionDecision = z.infer<typeof bookHealthExceptionDecisionSchema>;

export const mergeAccountsRequestSchema = z.object({
    /** The survivor: the Account that holds the source Client Reference. */
    survivorAccountId: z.string().uuid(),
    /** The rival, folded into the survivor and then orphaned. */
    loserAccountId: z.string().uuid(),
});
export type MergeAccountsRequest = z.infer<typeof mergeAccountsRequestSchema>;

export const mergeAccountsResponseSchema = z.object({
    survivorAccountId: z.string(),
    portfoliosRepointed: z.number(),
    identifiersRepointed: z.number(),
    /** True when the survivor took the holder of the loser. */
    holderAdopted: z.boolean(),
});
export type MergeAccountsResponse = z.infer<typeof mergeAccountsResponseSchema>;
